import { useState, useEffect } from 'react';

import { debounce } from 'lodash';

import BaseStore from '@Stores/BaseStore'
import allocationStore from 'modules/offer/stores/allocation'
import formStore from 'modules/offer/stores/form'
import offerStore from 'modules/offer/stores/offer'
import publisherStore from 'modules/offer/stores/publisher'
import PageStore from 'modules/path/stores/page'
import userProfileStore from 'modules/path/stores/user_profile';
import integrationStore from 'modules/settings/stores/integration'
import metaFieldStore from 'modules/settings/stores/meta-field'
import validatorStore from 'modules/settings/stores/validator'
import appStore from 'stores/app';

const loadOptions = (store, name = 'name', query) => debounce(async callback => {
  const response = await store.all(query);
  const data = response.data.map(({ [name]: label, id }) => ({ label: `${id} - ${label}`, value: id }))
  callback && callback(data)
  return data
}, 1000, { leading: true })

export const getAllocationOptions = loadOptions(allocationStore, 'description')
export const getPublisherOptions = loadOptions(publisherStore)
export const getOfferOptions = loadOptions(offerStore)
export const getFormOptions = loadOptions(formStore)
export const getValidatorOptions = loadOptions(validatorStore)
export const getPageOptions = loadOptions(PageStore)
export const getPathUserProfileOptions = loadOptions(userProfileStore)
export const getRevenueIntegrationOptions = loadOptions(integrationStore, 'description', {
  integration_type: ["revenue_postback", "revenue_poller"],
})
export const getDomainOptions = debounce(async callback => {
  const { domain_aliases } = appStore.site
  const data = domain_aliases.split(",")
    .filter(domain => !domain?.includes("*"))
    .map(domain => {
      domain = JSON.parse(domain)
      return { label: domain, value: domain }
    })
  callback && callback(data)
  return data
}, 1000, { leading: true })
export const getConsentDBAsOptions = debounce(async callback => {
  const consentDBAsStore = new class extends BaseStore {
    constructor() {
      super('dbas', 'dba', { site_model: true })
    }
  }
  const response = await consentDBAsStore.all()
  const data = response.data.map(({ label, value }) => ({ label, value }))
  callback && callback(data)
  return data
}, 1000, { leading: true })

function useStoreForOptions(loadOptions) {
  const [options, setOptions] = useState(null)

  useEffect(() => {
    !options && loadOptions(setOptions)
  }, [options])

  return options
}

export function useAllocationOptions() {
  return useStoreForOptions(getAllocationOptions)
}
export function usePublisherOptions() {
  return useStoreForOptions(getPublisherOptions)
}
export function useOfferOptions() {
  return useStoreForOptions(getOfferOptions)
}
export function useFormOptions() {
  return useStoreForOptions(getFormOptions)
}
export function usePageOptions() {
  return useStoreForOptions(getPageOptions)
}
export function usePathUserProfileOptions() {
  return useStoreForOptions(getPathUserProfileOptions)
}
export function useRevenueIntegrationOptions() {
  return useStoreForOptions(getRevenueIntegrationOptions)
}

export const booleanOptions = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

export const conditionOptions = [
  { label: 'Any', value: 'or' },
  { label: 'All', value: 'and' },
  { label: 'None', value: 'none' },
];

export const conditionsLabels = {
  none: "Neither",
}

export const getEqualityOptions = (type, origin) => {
  let options = [];

  if (type === 'integer' || type === 'float' || type === 'weekday' || type === 'time' || type === 'date' || type === 'datetime') {
    options = options.concat([
      { label: '<', value: 'lt' },
      { label: '>', value: 'gt' },
      { label: '≤', value: 'le' },
      { label: '≥', value: 'ge' },
    ]);
  }

  if (!['cert', 'dba'].includes(type)) {
    options = options.concat([
      { label: '=', value: 'equal' },
      { label: '≠', value: 'not-equal' },
    ]);
  }

  if (type === 'datetime') {
    options = options.concat([
      { label: 'Is within the last', value: 'within-last' },
      { label: 'Is not within the last', value: 'not-within-last' },
    ]);
  }

  if (!['form-visibility', 'form-populate'].includes(origin) && !['cert', 'boolean', 'fraud-score', 'domain', 'dba', 'cms-page'].includes(type)) {
    options = options.concat([
      { label: 'Is in validator', value: 'validator' },
    ]);
  }

  if (!['weekday', 'time', 'allocation', 'publisher', 'offer', 'form', 'integration-revenue', 'date', 'page', 'pathUserProfile', 'action'].includes(type)) {
    options = options.concat([
      { label: "Is Missing", value: 'missing' },
      { label: "Is Not Missing", value: 'not-missing' },
    ]);
    if (!['integer', 'float', 'boolean', 'cert', 'fraud-score', 'datetime'].includes(type)) {
      options = options.concat([
        { label: "Include Any", value: 'included', hint: 'Check if ANY of the itens selected on the right are in the request value.' },
        { label: "Include All", value: 'include-all', hint: 'Check if ALL of the itens selected on the right are in the request value.' },
        { label: "Exclude All", value: 'excluded', hint: 'Check if NONE of the itens selected on the right are in the request value.' },
        { label: 'Contains', value: 'contains' },
        { label: 'Starts With', value: 'start-with' },
        { label: 'Ends With', value: 'end-with' },
      ]);
    }
  }

  if (type === 'tcpaConsentDba') {
    options = options.filter(({ value }) => !['contains', 'start-with', 'end-with'].includes(value))
  } else if (type === 'domain' || type === 'cms-page') {
    options = options.filter(({ value }) => !['include-all', 'missing', 'not-missing'].includes(value))
  } else if (type === 'dba') {
    options = options.filter(({ value }) => !['missing', 'not-missing'].includes(value))
  }

  return options;
}

export const defaultOptions = [
  {
    key: 'formId',
    name: 'Form',
    type: 'form',
    group: 'Traffic',
  },
  {
    key: 'offerId',
    name: 'Offer',
    type: 'offer',
    group: 'Traffic',
  },
  {
    key: 'pid',
    name: 'Publisher',
    type: 'publisher',
    group: 'Traffic',
  },
  {
    key: 'allocationId',
    name: 'Allocation',
    type: 'allocation',
    group: 'Traffic',
  },
  {
    key: 'partnerId',
    name: 'Publisher (text)',
    type: 'input',
    group: 'Traffic',
  },
  {
    key: 'sid1',
    name: 'sid1',
    type: 'input',
    group: 'Traffic',
  },
  {
    key: 'sid2',
    name: 'sid2',
    type: 'input',
    group: 'Traffic',
  },
  {
    key: 'sid3',
    name: 'sid3',
    type: 'input',
    group: 'Traffic',
  },
  {
    key: 'domain',
    name: 'Site Domain',
    type: 'domain',
    group: 'Traffic',
  },
  {
    key: 'slotPosition',
    name: 'Slot Position',
    type: 'integer',
    group: 'Traffic',
  },
  {
    key: 'revenue',
    name: 'Revenue',
    type: 'float',
    group: 'Traffic',
  },
  {
    key: 'tcpaConsent',
    name: 'Is TCPA Consent ',
    type: 'boolean',
    group: 'Traffic',
  },
  {
    key: 'registrationConsent',
    name: 'Is Registration Consent ',
    type: 'boolean',
    group: 'Traffic',
  },
  {
    key: 'recordingConsent',
    name: 'Is Recording Consent ',
    type: 'boolean',
    group: 'Traffic',
  },
  {
    key: 'registrationCompleted',
    name: 'Is Registration Completed ',
    type: 'boolean',
    group: 'Traffic',
  },

  {
    key: 'registrationSkipped',
    name: 'Is Registration Skipped ',
    type: 'boolean',
    group: 'Traffic',
  },
  {
    key: 'duplicate',
    name: 'Is Duplicate',
    type: 'boolean',
    group: 'Traffic',
  },
  {
    key: 'duplicateClick',
    name: 'Is Duplicate Click',
    type: 'boolean',
    group: 'Traffic',
  },
  {
    key: 'duplicateLead',
    name: 'Is Duplicate Lead',
    type: 'boolean',
    group: 'Traffic',
  },
  {
    key: 'mobile',
    name: 'Is Mobile',
    type: 'boolean',
    group: 'Traffic',
  },
  {
    key: 'unsubscribed',
    name: 'Is Unsubscribed',
    type: 'boolean',
    group: 'Traffic',
  },
  {
    key: 'optout',
    name: 'Is Opt-out',
    type: 'boolean',
    group: 'Traffic',
  },
  {
    key: 'test',
    name: 'Is Test',
    type: 'boolean',
    group: 'Traffic',
  },
  {
    key: 'trustedForm',
    name: 'Trusted Form Cert',
    type: 'cert',
    group: 'Traffic',
  },
  {
    key: 'leadid',
    name: 'Leadid Cert',
    type: 'cert',
    group: 'Traffic',
  },
  {
    key: 'fraudscore',
    name: 'Fraud Score',
    type: 'fraud-score',
    group: 'Traffic',
  },
  {
    key: 'fraud',
    name: 'Is Fraud',
    type: 'boolean',
    group: 'Verifications',
  },
  {
    key: 'bot',
    name: 'Is Bot',
    type: 'boolean',
    group: 'Verifications',
  },
  {
    key: 'emailVerification',
    name: 'Is Email Valid',
    type: 'boolean',
    group: 'Verifications',
  },
  {
    key: 'phoneVerification',
    name: 'Is Phone Valid',
    type: 'boolean',
    group: 'Verifications',
  },
  {
    key: 'postalVerification',
    name: 'Is Postal Valid',
    type: 'boolean',
    group: 'Verifications',
  },
  {
    key: 'time',
    name: 'Time of Request',
    type: 'time',
    group: 'Day and Time',
  },
  {
    key: 'weekday',
    name: 'Day',
    type: 'weekday',
    group: 'Day and Time',
  },
];

export const genderOptions = [
  { label: "Male", value: "M" },
  { label: "Female", value: "F" },
]

export const weekdayOptions = [
  { label: 'Sunday', value: '0' },
  { label: 'Monday', value: '1' },
  { label: 'Tuesday', value: '2' },
  { label: 'Wednesday', value: '3' },
  { label: 'Thursday', value: '4' },
  { label: 'Friday', value: '5' },
  { label: 'Saturday', value: '6' },
]

export const fraudOptions = [
  { label: "Good", value: "good" },
  { label: "Bad", value: "bad" },
  { label: "Warn", value: "warn" },
  { label: "Drop", value: "drop" },
  { label: "Throttle", value: "throttle" },
]

const _changeGenderOptionType = userProfiles => {
  const genderOptionIndex = userProfiles.findIndex((e, i) => e.value === 'usr_gender');
  const genderOption = userProfiles[genderOptionIndex];
  genderOption.type = 'gender';
}

const _addAgeOption = userProfiles => {
  userProfiles.push({
    value: 'age',
    type: 'integer',
    label: 'Age',
    group: 'User Fields',
  })
}

const _addEmailOption = userProfiles => {
  userProfiles.push({
    value: 'usr_email',
    type: 'input',
    label: 'Email',
    group: 'User Fields',
  })
}

const _addRegistrationsOption = userProfiles => {
  userProfiles.push({
    value: 'registrations_count',
    type: 'integer',
    label: 'Registrations Count',
    group: 'User Fields',
  }, {
    value: 'last_registration',
    type: 'datetime',
    label: 'Last Registration',
    group: 'User Fields',
  })
}

export const getCustomOptions = debounce(async integrationType => {
  const userProfiles = (await userProfileStore.getFields()).map(key => ({ value: key.key, label: key.name, group: key.group, type: key.type, options: key.options }));
  const metaFields = (await metaFieldStore.all())?.data?.map(metaField => ({ value: metaField.key, label: `${metaField.key} (Meta Field)`, group: 'Traffic', type: 'input', options: null })) || [];

  userProfiles.push(...metaFields);

  if (['path_impression', 'path_ping_post', 'register'].includes(integrationType)) {
    userProfiles.push(
      ...userProfiles.filter(opt => opt.group === 'Custom Fields').map(opt => ({ ...opt, label: `${opt.label} (Is current session)`, group: 'Current Session', value: `${opt.value}_current_session`, type: opt.type, options: opt.options })),
    )
  }

  if (['path_impression', 'path_ping_post'].includes(integrationType)) {
    userProfiles.push(
      ...userProfiles.filter(opt => opt.group === 'Custom Fields').map(opt => ({ ...opt, label: `${opt.label} (Static DBA)`, group: 'Static DBAs', value: `${opt.value}_static_dba`, type: 'dba', options: null })),
    )
  }

  if (["revenue_share"].includes(integrationType)) {
    userProfiles.push({ value: 'integrationOrigin', type: 'integration-revenue', label: 'Integration Origin', group: 'Traffic' })
  }

  _changeGenderOptionType(userProfiles);
  _addAgeOption(userProfiles);
  _addEmailOption(userProfiles);
  _addRegistrationsOption(userProfiles);
  return userProfiles;
}, 500, { leading: true })

export const stateOptions = [
  { value: 'AL', label: "Alabama" },
  { value: 'AK', label: "Alaska" },
  { value: 'AZ', label: "Arizona" },
  { value: 'AR', label: "Arkansas" },
  { value: 'CA', label: "California" },
  { value: 'CO', label: "Colorado" },
  { value: 'CT', label: "Connecticut" },
  { value: 'DE', label: "Delaware" },
  { value: 'DC', label: "District Of Columbia" },
  { value: 'FL', label: "Florida" },
  { value: 'GA', label: "Georgia" },
  { value: 'HI', label: "Hawaii" },
  { value: 'ID', label: "Idaho" },
  { value: 'IL', label: "Illinois" },
  { value: 'IN', label: "Indiana" },
  { value: 'IA', label: "Iowa" },
  { value: 'KS', label: "Kansas" },
  { value: 'KY', label: "Kentucky" },
  { value: 'LA', label: "Louisiana" },
  { value: 'ME', label: "Maine" },
  { value: 'MD', label: "Maryland" },
  { value: 'MA', label: "Massachusetts" },
  { value: 'MI', label: "Michigan" },
  { value: 'MN', label: "Minnesota" },
  { value: 'MS', label: "Mississippi" },
  { value: 'MO', label: "Missouri" },
  { value: 'MT', label: "Montana" },
  { value: 'NE', label: "Nebraska" },
  { value: 'NV', label: "Nevada" },
  { value: 'NH', label: "New Hampshire" },
  { value: 'NJ', label: "New Jersey" },
  { value: 'NM', label: "New Mexico" },
  { value: 'NY', label: "New York" },
  { value: 'NC', label: "North Carolina" },
  { value: 'ND', label: "North Dakota" },
  { value: 'OH', label: "Ohio" },
  { value: 'OK', label: "Oklahoma" },
  { value: 'OR', label: "Oregon" },
  { value: 'PA', label: "Pennsylvania" },
  { value: 'PR', label: "Puerto Rico" },
  { value: 'RI', label: "Rhode Island" },
  { value: 'SC', label: "South Carolina" },
  { value: 'SD', label: "South Dakota" },
  { value: 'TN', label: "Tennessee" },
  { value: 'TX', label: "Texas" },
  { value: 'UT', label: "Utah" },
  { value: 'VT', label: "Vermont" },
  { value: 'VA', label: "Virginia" },
  { value: 'WA', label: "Washington" },
  { value: 'WV', label: "West Virginia" },
  { value: 'WI', label: "Wisconsin" },
  { value: 'WY', label: "Wyoming" },
]

export const monthOptions = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]

export const dayOptions = () => {
  const options = []
  for (let index = 1; index <= 31; index++) {
    options.push({ value: index, label: index })
  }
  return options
}

export const yearOptions = () => {
  const options = []
  for (let index = 2002; index >= 1920; index--) {
    options.push({ value: index, label: index })
  }
  return options
}

export const countryList = [
  { name: 'United States', code: 'US' },
  { name: 'Afghanistan', code: 'AF' },
  { name: 'Åland Islands', code: 'AX' },
  { name: 'Albania', code: 'AL' },
  { name: 'Algeria', code: 'DZ' },
  { name: 'American Samoa', code: 'AS' },
  { name: 'AndorrA', code: 'AD' },
  { name: 'Angola', code: 'AO' },
  { name: 'Anguilla', code: 'AI' },
  { name: 'Antarctica', code: 'AQ' },
  { name: 'Antigua and Barbuda', code: 'AG' },
  { name: 'Argentina', code: 'AR' },
  { name: 'Armenia', code: 'AM' },
  { name: 'Aruba', code: 'AW' },
  { name: 'Australia', code: 'AU' },
  { name: 'Austria', code: 'AT' },
  { name: 'Azerbaijan', code: 'AZ' },
  { name: 'Bahamas', code: 'Bvai ficar um monte de dado S' },
  { name: 'Bahrain', code: 'BH' },
  { name: 'Bangladesh', code: 'BD' },
  { name: 'Barbados', code: 'BB' },
  { name: 'Belarus', code: 'BY' },
  { name: 'Belgium', code: 'BE' },
  { name: 'Belize', code: 'BZ' },
  { name: 'Benin', code: 'BJ' },
  { name: 'Bermuda', code: 'BM' },
  { name: 'Bhutan', code: 'BT' },
  { name: 'Bolivia', code: 'BO' },
  { name: 'Bosnia and Herzegovina', code: 'BA' },
  { name: 'Botswana', code: 'BW' },
  { name: 'Bouvet Island', code: 'BV' },
  { name: 'Brazil', code: 'BR' },
  { name: 'British Indian Ocean Territory', code: 'IO' },
  { name: 'Brunei Darussalam', code: 'BN' },
  { name: 'Bulgaria', code: 'BG' },
  { name: 'Burkina Faso', code: 'BF' },
  { name: 'Burundi', code: 'BI' },
  { name: 'Cambodia', code: 'KH' },
  { name: 'Cameroon', code: 'CM' },
  { name: 'Canada', code: 'CA' },
  { name: 'Cape Verde', code: 'CV' },
  { name: 'Cayman Islands', code: 'KY' },
  { name: 'Central African Republic', code: 'CF' },
  { name: 'Chad', code: 'TD' },
  { name: 'Chile', code: 'CL' },
  { name: 'China', code: 'CN' },
  { name: 'Christmas Island', code: 'CX' },
  { name: 'Cocos (Keeling) Islands', code: 'CC' },
  { name: 'Colombia', code: 'CO' },
  { name: 'Comoros', code: 'KM' },
  { name: 'Congo', code: 'CG' },
  { name: 'Congo, The Democratic Republic of the', code: 'CD' },
  { name: 'Cook Islands', code: 'CK' },
  { name: 'Costa Rica', code: 'CR' },
  { name: 'Cote D\'Ivoire', code: 'CI' },
  { name: 'Croatia', code: 'HR' },
  { name: 'Cuba', code: 'CU' },
  { name: 'Cyprus', code: 'CY' },
  { name: 'Czech Republic', code: 'CZ' },
  { name: 'Denmark', code: 'DK' },
  { name: 'Djibouti', code: 'DJ' },
  { name: 'Dominica', code: 'DM' },
  { name: 'Dominican Republic', code: 'DO' },
  { name: 'Ecuador', code: 'EC' },
  { name: 'Egypt', code: 'EG' },
  { name: 'El Salvador', code: 'SV' },
  { name: 'Equatorial Guinea', code: 'GQ' },
  { name: 'Eritrea', code: 'ER' },
  { name: 'Estonia', code: 'EE' },
  { name: 'Ethiopia', code: 'ET' },
  { name: 'Falkland Islands (Malvinas)', code: 'FK' },
  { name: 'Faroe Islands', code: 'FO' },
  { name: 'Fiji', code: 'FJ' },
  { name: 'Finland', code: 'FI' },
  { name: 'France', code: 'FR' },
  { name: 'French Guiana', code: 'GF' },
  { name: 'French Polynesia', code: 'PF' },
  { name: 'French Southern Territories', code: 'TF' },
  { name: 'Gabon', code: 'GA' },
  { name: 'Gambia', code: 'GM' },
  { name: 'Georgia', code: 'GE' },
  { name: 'Germany', code: 'DE' },
  { name: 'Ghana', code: 'GH' },
  { name: 'Gibraltar', code: 'GI' },
  { name: 'Greece', code: 'GR' },
  { name: 'Greenland', code: 'GL' },
  { name: 'Grenada', code: 'GD' },
  { name: 'Guadeloupe', code: 'GP' },
  { name: 'Guam', code: 'GU' },
  { name: 'Guatemala', code: 'GT' },
  { name: 'Guernsey', code: 'GG' },
  { name: 'Guinea', code: 'GN' },
  { name: 'Guinea-Bissau', code: 'GW' },
  { name: 'Guyana', code: 'GY' },
  { name: 'Haiti', code: 'HT' },
  { name: 'Heard Island and Mcdonald Islands', code: 'HM' },
  { name: 'Holy See (Vatican City State)', code: 'VA' },
  { name: 'Honduras', code: 'HN' },
  { name: 'Hong Kong', code: 'HK' },
  { name: 'Hungary', code: 'HU' },
  { name: 'Iceland', code: 'IS' },
  { name: 'India', code: 'IN' },
  { name: 'Indonesia', code: 'ID' },
  { name: 'Iran, Islamic Republic Of', code: 'IR' },
  { name: 'Iraq', code: 'IQ' },
  { name: 'Ireland', code: 'IE' },
  { name: 'Isle of Man', code: 'IM' },
  { name: 'Israel', code: 'IL' },
  { name: 'Italy', code: 'IT' },
  { name: 'Jamaica', code: 'JM' },
  { name: 'Japan', code: 'JP' },
  { name: 'Jersey', code: 'JE' },
  { name: 'Jordan', code: 'JO' },
  { name: 'Kazakhstan', code: 'KZ' },
  { name: 'Kenya', code: 'KE' },
  { name: 'Kiribati', code: 'KI' },
  { name: 'Korea, Democratic People\'S Republic of', code: 'KP' },
  { name: 'Korea, Republic of', code: 'KR' },
  { name: 'Kuwait', code: 'KW' },
  { name: 'Kyrgyzstan', code: 'KG' },
  { name: 'Lao People\'S Democratic Republic', code: 'LA' },
  { name: 'Latvia', code: 'LV' },
  { name: 'Lebanon', code: 'LB' },
  { name: 'Lesotho', code: 'LS' },
  { name: 'Liberia', code: 'LR' },
  { name: 'Libyan Arab Jamahiriya', code: 'LY' },
  { name: 'Liechtenstein', code: 'LI' },
  { name: 'Lithuania', code: 'LT' },
  { name: 'Luxembourg', code: 'LU' },
  { name: 'Macao', code: 'MO' },
  { name: 'Macedonia, The Former Yugoslav Republic of', code: 'MK' },
  { name: 'Madagascar', code: 'MG' },
  { name: 'Malawi', code: 'MW' },
  { name: 'Malaysia', code: 'MY' },
  { name: 'Maldives', code: 'MV' },
  { name: 'Mali', code: 'ML' },
  { name: 'Malta', code: 'MT' },
  { name: 'Marshall Islands', code: 'MH' },
  { name: 'Martinique', code: 'MQ' },
  { name: 'Mauritania', code: 'MR' },
  { name: 'Mauritius', code: 'MU' },
  { name: 'Mayotte', code: 'YT' },
  { name: 'Mexico', code: 'MX' },
  { name: 'Micronesia, Federated States of', code: 'FM' },
  { name: 'Moldova, Republic of', code: 'MD' },
  { name: 'Monaco', code: 'MC' },
  { name: 'Mongolia', code: 'MN' },
  { name: 'Montserrat', code: 'MS' },
  { name: 'Morocco', code: 'MA' },
  { name: 'Mozambique', code: 'MZ' },
  { name: 'Myanmar', code: 'MM' },
  { name: 'Namibia', code: 'NA' },
  { name: 'Nauru', code: 'NR' },
  { name: 'Nepal', code: 'NP' },
  { name: 'Netherlands', code: 'NL' },
  { name: 'Netherlands Antilles', code: 'AN' },
  { name: 'New Caledonia', code: 'NC' },
  { name: 'New Zealand', code: 'NZ' },
  { name: 'Nicaragua', code: 'NI' },
  { name: 'Niger', code: 'NE' },
  { name: 'Nigeria', code: 'NG' },
  { name: 'Niue', code: 'NU' },
  { name: 'Norfolk Island', code: 'NF' },
  { name: 'Northern Mariana Islands', code: 'MP' },
  { name: 'Norway', code: 'NO' },
  { name: 'Oman', code: 'OM' },
  { name: 'Pakistan', code: 'PK' },
  { name: 'Palau', code: 'PW' },
  { name: 'Palestinian Territory, Occupied', code: 'PS' },
  { name: 'Panama', code: 'PA' },
  { name: 'Papua New Guinea', code: 'PG' },
  { name: 'Paraguay', code: 'PY' },
  { name: 'Peru', code: 'PE' },
  { name: 'Philippines', code: 'PH' },
  { name: 'Pitcairn', code: 'PN' },
  { name: 'Poland', code: 'PL' },
  { name: 'Portugal', code: 'PT' },
  { name: 'Puerto Rico', code: 'PR' },
  { name: 'Qatar', code: 'QA' },
  { name: 'Reunion', code: 'RE' },
  { name: 'Romania', code: 'RO' },
  { name: 'Russian Federation', code: 'RU' },
  { name: 'RWANDA', code: 'RW' },
  { name: 'Saint Helena', code: 'SH' },
  { name: 'Saint Kitts and Nevis', code: 'KN' },
  { name: 'Saint Lucia', code: 'LC' },
  { name: 'Saint Pierre and Miquelon', code: 'PM' },
  { name: 'Saint Vincent and the Grenadines', code: 'VC' },
  { name: 'Samoa', code: 'WS' },
  { name: 'San Marino', code: 'SM' },
  { name: 'Sao Tome and Principe', code: 'ST' },
  { name: 'Saudi Arabia', code: 'SA' },
  { name: 'Senegal', code: 'SN' },
  { name: 'Serbia and Montenegro', code: 'CS' },
  { name: 'Seychelles', code: 'SC' },
  { name: 'Sierra Leone', code: 'SL' },
  { name: 'Singapore', code: 'SG' },
  { name: 'Slovakia', code: 'SK' },
  { name: 'Slovenia', code: 'SI' },
  { name: 'Solomon Islands', code: 'SB' },
  { name: 'Somalia', code: 'SO' },
  { name: 'South Africa', code: 'ZA' },
  { name: 'South Georgia and the South Sandwich Islands', code: 'GS' },
  { name: 'Spain', code: 'ES' },
  { name: 'Sri Lanka', code: 'LK' },
  { name: 'Sudan', code: 'SD' },
  { name: 'Suriname', code: 'SR' },
  { name: 'Svalbard and Jan Mayen', code: 'SJ' },
  { name: 'Swaziland', code: 'SZ' },
  { name: 'Sweden', code: 'SE' },
  { name: 'Switzerland', code: 'CH' },
  { name: 'Syrian Arab Republic', code: 'SY' },
  { name: 'Taiwan, Province of China', code: 'TW' },
  { name: 'Tajikistan', code: 'TJ' },
  { name: 'Tanzania, United Republic of', code: 'TZ' },
  { name: 'Thailand', code: 'TH' },
  { name: 'Timor-Leste', code: 'TL' },
  { name: 'Togo', code: 'TG' },
  { name: 'Tokelau', code: 'TK' },
  { name: 'Tonga', code: 'TO' },
  { name: 'Trinidad and Tobago', code: 'TT' },
  { name: 'Tunisia', code: 'TN' },
  { name: 'Turkey', code: 'TR' },
  { name: 'Turkmenistan', code: 'TM' },
  { name: 'Turks and Caicos Islands', code: 'TC' },
  { name: 'Tuvalu', code: 'TV' },
  { name: 'Uganda', code: 'UG' },
  { name: 'Ukraine', code: 'UA' },
  { name: 'United Arab Emirates', code: 'AE' },
  { name: 'United Kingdom', code: 'GB' },
  { name: 'United States', code: 'US' },
  { name: 'United States Minor Outlying Islands', code: 'UM' },
  { name: 'Uruguay', code: 'UY' },
  { name: 'Uzbekistan', code: 'UZ' },
  { name: 'Vanuatu', code: 'VU' },
  { name: 'Venezuela', code: 'VE' },
  { name: 'Viet Nam', code: 'VN' },
  { name: 'Virgin Islands, British', code: 'VG' },
  { name: 'Virgin Islands, U.S.', code: 'VI' },
  { name: 'Wallis and Futuna', code: 'WF' },
  { name: 'Western Sahara', code: 'EH' },
  { name: 'Yemen', code: 'YE' },
  { name: 'Zambia', code: 'ZM' },
  { name: 'Zimbabwe', code: 'ZW' },
]

export const getCountryOptions = () => countryList.map(o => ({ label: o.name, value: o.code }))

export const buttonLabels = {
  'no-conflict': 'Save',
  'conflict': 'Review your rules',
  'checking': 'Checking for conflicts...',
}
